<script lang="ts" setup>
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { routerPush, routerReplace } from '../../../../helpers/routing';
import Checkout from '../../components/checkout/checkout.vue';
import { useCheckoutStateStore } from '../../components/checkout/stores/checkoutStateStore';
import CheckoutPageLoading from '../../components/shoppingCart/checkoutPageLoading.vue';
import OrderPlacementLoading from '../../components/shoppingCart/orderPlacementLoading.vue';
import ShoppingCart from '../../components/shoppingCart/shoppingCart.vue';
import { useShoppingCartStore } from '../../components/shoppingCart/stores/shoppingCartStore';
import { CheckoutStages, CheckoutSteps } from '../../services/models/checkout.models';
import { useFavoritesStore } from '../../stores/favoritesStore';
import { Cart, PlaceholderAddressModel } from '../../utils/API/Cart/cartAPISchema';
import { Log } from '../../utils/API/Logging/LoggingAPI';
import {
    buildCheckoutNavigation,
    getRouteStage,
    resolveStageAfterBfcacheRestore,
} from './checkoutPage.routeSync';

const props = defineProps<{
  ksps: string | null; // JSON Content from NewCheckoutIndex.cshtml
  checkoutLoginCta: string | null; // JSON Content from NewCheckoutIndex.cshtml
  receiptPageUrl: string | null;
  netsEasyApiCheckoutKey: string | null;
  showInventoryNotification: string | null;
  clickAndCollectEnabled: string | null;
  paymentId: any; // Payment ID from Nets Easy
  maxCartSize?: string;
  initialLineItemCount?: string; // Number of line items in the cart when the component is mounted
  paymentLogos: string;
}>();

const checkoutStateStore = useCheckoutStateStore();
const shoppingCartStore = useShoppingCartStore();
const favoritesStore = useFavoritesStore();
const route = useRoute();
const router = useRouter();
const cart = ref<Cart>();
const customerInformation = ref<PlaceholderAddressModel>();
const syncDirection = ref<'fromRoute' | 'toRoute' | null>(null);
const isMounted = ref(false);

const syncRouteToCheckoutState = async () => {
  if (!route || !isMounted.value) {
    return;
  }

  // Go directly to shoppingcart if cart is empty to avoid invalid checkout states
  if (shoppingCartStore.cart && shoppingCartStore.cart.itemCount === 0) {
    await checkoutStateStore.goToStage(CheckoutStages.ShoppingCart);
    return;
  }

  const routeStage = getRouteStage(route.path);

  if (!routeStage) {
    return;
  }

  const stageAlreadySynced = routeStage === checkoutStateStore.currentCheckoutStage;

  if (stageAlreadySynced) {
    return;
  }

  syncDirection.value = 'fromRoute';
  await checkoutStateStore.goToStage(routeStage);
  syncDirection.value = null;
};

const syncCheckoutStateToRoute = () => {
  if (!route || !isMounted.value) {
    return;
  }

  const nextNavigation = buildCheckoutNavigation(
    route.path,
    checkoutStateStore.currentCheckoutStage,
  );

  if (!nextNavigation) {
    return;
  }

  syncDirection.value = 'toRoute';
  const hasCheckoutStateInCurrentRoute = !!getRouteStage(route.path);

  if (hasCheckoutStateInCurrentRoute && router) {
    routerPush(nextNavigation, router);
  } else if (router) {
    routerReplace(nextNavigation, router);
  }

  syncDirection.value = null;
};

/**
 * Initializes the shopping cart by setting the loading states for the checkout and upsell stores,
 * then fetching the shopping cart and upsell model data from the API.
 *
 * @async
 * @function initShoppingCart
 * @returns {Promise<void>} A promise that resolves when the shopping cart and upsell model data have been fetched and the loading states have been updated.
 */
const initShoppingCart = async () => {
  shoppingCartStore.setCart(null);

  if (props.receiptPageUrl) {
    checkoutStateStore.setReceiptPageUrl(props.receiptPageUrl);
  }

  if (props.netsEasyApiCheckoutKey) {
    checkoutStateStore.setNetsEasyApiCheckoutKey(props.netsEasyApiCheckoutKey);
  }

  if (props.clickAndCollectEnabled) {
    checkoutStateStore.setClickAndCollectEnabled(props.clickAndCollectEnabled);
  }

  if (props.paymentLogos) {
    shoppingCartStore.setPaymentLogos(JSON.parse(props.paymentLogos));
  }

  const hasCheckoutStateInRoute = !!getRouteStage(route.path);

  // If a paymentId exists at page load, the user is returning from a Nets Easy
  // redirect (e.g. 3D Secure challenge). Go straight to the Payment step so
  // PaymentInformationForm mounts and the SDK can re-fire payment-completed.
  // Skip cart loading, the webhook may have already cleared the cart.
  let paymentId: unknown = null;
  try {
    paymentId = JSON.parse(props.paymentId)?.value;
  } catch (e) {
    Log({ LogLevel: 'Warning', Message: `initShoppingCart: failed to parse paymentId prop: ${props.paymentId}` });
  }
  if (paymentId) {
    checkoutStateStore.setPaymentId(paymentId as string);
    checkoutStateStore.setCheckoutStage(CheckoutStages.Checkout);
    checkoutStateStore.setCheckoutStep(CheckoutSteps.Payment);
    return;
  }

  if (!hasCheckoutStateInRoute) {
    checkoutStateStore.setCheckoutStage(CheckoutStages.ShoppingCart);
  }

  // Need to explicitly set loading of cart to trigger skeleton loaders
  checkoutStateStore.setIsLoadingCart(true);
  checkoutStateStore.setIsLoading(true);

  const newCart = await checkoutStateStore.loadShoppingCart();
  if (newCart) {
    shoppingCartStore.setCart(newCart);
  }

  checkoutStateStore.setIsLoadingCart(false);
  checkoutStateStore.setIsLoading(false);

  if (shoppingCartStore.cart?.isAuthenticated && !favoritesStore.favorites) {
    favoritesStore.getFavorites();
  }
};

// A bfcache-restored pageshow freezes the page's JS state as of unload, which
// can leave it stuck on the one-way PlacingOrder stage after a Back navigation
// from the receipt page.
const handlePageShow = (event: PageTransitionEvent) => {
  if (!event.persisted) return;

  const restoredStage = resolveStageAfterBfcacheRestore(checkoutStateStore.currentCheckoutStage);
  if (restoredStage) {
    checkoutStateStore.setCheckoutStage(restoredStage);
  }
};

onMounted(async () => {
  window.history.scrollRestoration = 'manual';
  window.scrollTo({ top: 0, behavior: 'instant' });
  isMounted.value = true;
  await initShoppingCart();

  // Skip route sync when returning from a payment redirect — the paymentId
  // recovery path already set the correct stage/step, and route sync would
  // override it back to ShoppingCart (the URL path is still the checkout root).
  if (!checkoutStateStore.paymentId) {
    await syncRouteToCheckoutState();
  }

  if (props.ksps) {
    shoppingCartStore.setKsps(JSON.parse(props.ksps));
  }

  if (props.checkoutLoginCta) {
    shoppingCartStore.setCheckoutLoginCta(JSON.parse(props.checkoutLoginCta));
  }

  window.addEventListener('pageshow', handlePageShow);
});

onUnmounted(() => {
  window.removeEventListener('pageshow', handlePageShow);
});

watch([() => route.path, () => route.query], async () => {
  if (syncDirection.value === 'toRoute') {
    return;
  }

  await syncRouteToCheckoutState();
});

watch(
  [() => checkoutStateStore.currentCheckoutStage, () => checkoutStateStore.currentCheckoutStep],
  () => {
    if (syncDirection.value === 'fromRoute') {
      return;
    }

    syncCheckoutStateToRoute();
  }
);
</script>

<template>
  <div>
    <CheckoutPageLoading
      v-if="checkoutStateStore.currentCheckoutStage === CheckoutStages.InitialPageLoad"
    ></CheckoutPageLoading>
    <OrderPlacementLoading
      v-if="checkoutStateStore.currentCheckoutStage === CheckoutStages.PlacingOrder"
    ></OrderPlacementLoading>
    <ShoppingCart
      v-if="checkoutStateStore.currentCheckoutStage === CheckoutStages.ShoppingCart"
      :ksps="props.ksps"
      :max-cart-size="props.maxCartSize"
      :initial-line-item-count="props.initialLineItemCount"
      :is-quick-add="true"
      :show-inventory-notification="props.showInventoryNotification === 'true'"
    />
    <Checkout
      v-if="checkoutStateStore.currentCheckoutStage === CheckoutStages.Checkout"
      :customer-information="customerInformation"
      :cart="cart"
    />
  </div>
</template>
